import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  CircularProgress,
  MenuItem,
  Alert
} from '@mui/material';
import { ArrowBack, Dashboard } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { superAdminAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { useFormValidation } from '@/hooks/useFormValidation';
import { required, email } from '@/utils/validators';

const SchoolForm = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);
  const [sendingResetLink, setSendingResetLink] = useState(false);
  const [error, setError] = useState('');
  const [originalStatus, setOriginalStatus] = useState('Active');
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    status: 'Active',
    // Principal/Admin credentials
    principal_name: '',
    principal_email: '',
    principal_phone: ''
  });

  const schema = {
    name: [required('School name')],
    address: [required('Address')],
    city: [required('City')],
    state: [required('State')],
    postal_code: [required('Postal code')],
    country: [required('Country')],
    contact_person: [required('Contact person name')],
    contact_email: [required('Contact email'), email],
    contact_phone: [required('Contact phone')],
    ...(isEditMode ? {} : {
      principal_name: [required('Principal/Admin name')],
      principal_email: [required('Principal/Admin email'), email],
      principal_phone: [required('Principal/Admin phone')],
    }),
  };
  const { handleBlur, validateAll, applyServerErrors, showError } = useFormValidation(schema);

  const fetchSchool = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await superAdminAPI.getSchool(id);
      setFormData(response.data.school);
      setOriginalStatus(response.data.school.status || 'Active');
    } catch (error) {
      toast.error('Failed to load school data');
      console.error(error);
      navigate('/superadmin/schools');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (isEditMode) {
      fetchSchool();
    }
  }, [isEditMode, fetchSchool]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateAll(formData)) return;
    setSubmitting(true);
    setError('');

    try {
      if (isEditMode) {
        await superAdminAPI.updateSchool(id, formData);
        if (formData.status !== originalStatus) {
          await superAdminAPI.updateSchoolStatus(id, formData.status);
        }
        toast.success('School updated successfully');
      } else {
        await superAdminAPI.createSchool(formData);
        toast.success('School created successfully');
      }
      navigate('/superadmin/schools');
    } catch (error) {
      const serverErrors = error.response?.data?.errors;
      if (serverErrors) {
        applyServerErrors(serverErrors);
      } else {
        setError(error.response?.data?.message || 'Failed to save school');
      }
      setSubmitting(false);
    }
  };

  const handleSendResetLink = async () => {
    setSendingResetLink(true);
    setError('');
    try {
      await superAdminAPI.sendPrincipalResetLink(id);
      toast.success('Password reset link sent to the principal\'s email');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send reset link');
    } finally {
      setSendingResetLink(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          {isEditMode ? 'Edit School' : 'Add New School'}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="School Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onBlur={(e) => handleBlur('name', e.target.value)}
                error={Boolean(showError('name'))}
                helperText={showError('name')}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                onBlur={(e) => handleBlur('address', e.target.value)}
                error={Boolean(showError('address'))}
                helperText={showError('address')}
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="City"
                name="city"
                value={formData.city}
                onChange={handleChange}
                onBlur={(e) => handleBlur('city', e.target.value)}
                error={Boolean(showError('city'))}
                helperText={showError('city')}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="State"
                name="state"
                value={formData.state}
                onChange={handleChange}
                onBlur={(e) => handleBlur('state', e.target.value)}
                error={Boolean(showError('state'))}
                helperText={showError('state')}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Postal Code"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleChange}
                onBlur={(e) => handleBlur('postal_code', e.target.value)}
                error={Boolean(showError('postal_code'))}
                helperText={showError('postal_code')}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Country"
                name="country"
                value={formData.country}
                onChange={handleChange}
                onBlur={(e) => handleBlur('country', e.target.value)}
                error={Boolean(showError('country'))}
                helperText={showError('country')}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Contact Information
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Contact Person Name"
                name="contact_person"
                value={formData.contact_person}
                onChange={handleChange}
                onBlur={(e) => handleBlur('contact_person', e.target.value)}
                error={Boolean(showError('contact_person'))}
                helperText={showError('contact_person')}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="email"
                label="Contact Email"
                name="contact_email"
                value={formData.contact_email}
                onChange={handleChange}
                onBlur={(e) => handleBlur('contact_email', e.target.value)}
                error={Boolean(showError('contact_email'))}
                helperText={showError('contact_email')}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Contact Phone"
                name="contact_phone"
                value={formData.contact_phone}
                onChange={handleChange}
                onBlur={(e) => handleBlur('contact_phone', e.target.value)}
                error={Boolean(showError('contact_phone'))}
                helperText={showError('contact_phone')}
              />
            </Grid>

            {!isEditMode && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    School Admin/Principal Credentials
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Enter the school administrator's details. A password setup link will be emailed to them.
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Principal/Admin Name"
                    name="principal_name"
                    value={formData.principal_name}
                    onChange={handleChange}
                    onBlur={(e) => handleBlur('principal_name', e.target.value)}
                    error={Boolean(showError('principal_name'))}
                    helperText={showError('principal_name')}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    type="email"
                    label="Principal/Admin Email (Login Username)"
                    name="principal_email"
                    value={formData.principal_email}
                    onChange={handleChange}
                    onBlur={(e) => handleBlur('principal_email', e.target.value)}
                    error={Boolean(showError('principal_email'))}
                    helperText={showError('principal_email') || 'This will be used as the login username'}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Principal/Admin Phone"
                    name="principal_phone"
                    value={formData.principal_phone}
                    onChange={handleChange}
                    onBlur={(e) => handleBlur('principal_phone', e.target.value)}
                    error={Boolean(showError('principal_phone'))}
                    helperText={showError('principal_phone')}
                  />
                </Grid>
              </>
            )}

            {isEditMode && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </TextField>
              </Grid>
            )}

            {isEditMode && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Reset Principal Password
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Send the school's principal a link to reset their password. The link expires in 1 hour.
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    onClick={handleSendResetLink}
                    disabled={sendingResetLink}
                  >
                    {sendingResetLink ? 'Sending...' : 'Send Password Reset Link'}
                  </Button>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/superadmin/dashboard')}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={submitting}
                >
                  {submitting ? <CircularProgress size={24} /> : (isEditMode ? 'Update School' : 'Create School')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default SchoolForm;
